import { useCallback } from 'react';
import { usePersistState } from 'src/hooks/usePersistState';

import { PersistedPreviewSession } from '../types';

const STORAGE_KEY = 'frida_preview_sessions';

/**
 * Storage layer for preview sessions (acts as a local database).
 * Manages read/write to localStorage under a single key.
 * All entries are stored without status — status is a UI concern.
 */
export const usePreviewSessionStorage = () => {
  const [persistedSessions, setPersistedSessions] = usePersistState<PersistedPreviewSession[]>(
    STORAGE_KEY,
    [],
  );

  /** Returns all non-expired sessions from storage */
  const getPersistedSessions = useCallback((): PersistedPreviewSession[] => {
    return persistedSessions.filter((s) => s.expiresAt > Date.now());
  }, [persistedSessions]);

  /** Adds or replaces a session in storage (deduplicates by deviceId) */
  const persistSession = useCallback(
    (session: PersistedPreviewSession) => {
      setPersistedSessions((prev) => [
        ...prev.filter((s) => s.deviceId !== session.deviceId),
        session,
      ]);
    },
    [setPersistedSessions],
  );

  /** Removes a session from storage by deviceId (no-op if not found) */
  const deleteSession = useCallback(
    (deviceId: string) => {
      setPersistedSessions((prev) => {
        const remainingSessions = prev.filter((s) => s.deviceId !== deviceId);
        return remainingSessions.length === prev.length ? prev : remainingSessions;
      });
    },
    [setPersistedSessions],
  );

  /** Removes all expired sessions from storage */
  const deleteExpiredSessions = useCallback(() => {
    setPersistedSessions((prev) => {
      const now = Date.now();
      const nonExpiredSessions = prev.filter((s) => s.expiresAt > now);
      return nonExpiredSessions.length === prev.length ? prev : nonExpiredSessions;
    });
  }, [setPersistedSessions]);

  return { getPersistedSessions, persistSession, deleteSession, deleteExpiredSessions };
};
